import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, Trash2, Eye, Download, Loader2, UploadCloud, FileText, Image as ImageIcon, FileSpreadsheet, File as FileIcon } from "lucide-react";
import { api, API, formatApiError } from "@/lib/api";
import { fmtBytes, fmtTid, vedleggKategori } from "./sakerUtils";
import VedleggViser from "./VedleggViser";

const CHUNK = 4 * 1024 * 1024;
const MAKS = 25 * 1024 * 1024;

const IKON = {
  pdf: { Ikon: FileText, farge: "text-red-500", bg: "bg-red-50" },
  word: { Ikon: FileText, farge: "text-blue-600", bg: "bg-blue-50" },
  bilde: { Ikon: ImageIcon, farge: "text-emerald-600", bg: "bg-emerald-50" },
  regneark: { Ikon: FileSpreadsheet, farge: "text-green-600", bg: "bg-green-50" },
  tekst: { Ikon: FileText, farge: "text-neutral-500", bg: "bg-neutral-100" },
  fil: { Ikon: FileIcon, farge: "text-neutral-500", bg: "bg-neutral-100" },
};

async function lastOppFil(sakId, fil, aktor, onProgress) {
  const uploadId = crypto.randomUUID();
  const total = Math.max(1, Math.ceil(fil.size / CHUNK));
  let siste = null;
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("chunk", fil.slice(i * CHUNK, (i + 1) * CHUNK));
    fd.append("uploadId", uploadId);
    fd.append("index", String(i));
    fd.append("total", String(total));
    fd.append("name", fil.name);
    fd.append("contentType", fil.type || "");
    fd.append("actor", aktor || "Admin");
    const { data } = await api.post(`/admin/tasks/${sakId}/attachments/chunk`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    onProgress(Math.round(((i + 1) / total) * 100));
    siste = data;
  }
  return siste;
}

export default function Vedlegg({ sak, aktor, onReplaceTask }) {
  const [drar, setDrar] = useState(false);
  const [opplastinger, setOpplastinger] = useState([]);
  const [viserIdx, setViserIdx] = useState(null);
  const inputRef = useRef(null);
  const dragTeller = useRef(0);
  const vedlegg = sak.attachments || [];

  useEffect(() => { setViserIdx(null); }, [sak.id]);

  const lastOpp = async (filer) => {
    for (const fil of Array.from(filer || [])) {
      if (fil.size > MAKS) {
        toast.error(`«${fil.name}» er større enn 25 MB`);
        continue;
      }
      const oid = crypto.randomUUID();
      setOpplastinger((prev) => [...prev, { id: oid, navn: fil.name, pct: 0 }]);
      try {
        const res = await lastOppFil(sak.id, fil, aktor, (pct) =>
          setOpplastinger((prev) => prev.map((o) => (o.id === oid ? { ...o, pct } : o)))
        );
        if (res?.task) onReplaceTask(res.task);
        toast.success(`«${fil.name}» lastet opp`);
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || `Kunne ikke laste opp «${fil.name}»`);
      } finally {
        setOpplastinger((prev) => prev.filter((o) => o.id !== oid));
      }
    }
  };

  const slett = async (att) => {
    if (!window.confirm(`Slette «${att.name}»?`)) return;
    try {
      const { data } = await api.delete(`/admin/tasks/${sak.id}/attachments/${att.id}`, { params: { actor: aktor } });
      if (data.task) onReplaceTask(data.task);
      toast.success("Vedlegget ble slettet");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Kunne ikke slette vedlegget");
    }
  };

  return (
    <div
      className="mt-7 relative"
      onDragEnter={(e) => { e.preventDefault(); dragTeller.current += 1; setDrar(true); }}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={() => { dragTeller.current -= 1; if (dragTeller.current <= 0) { dragTeller.current = 0; setDrar(false); } }}
      onDrop={(e) => { e.preventDefault(); dragTeller.current = 0; setDrar(false); lastOpp(e.dataTransfer.files); }}
      data-testid="sak-vedlegg"
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10.5px] font-semibold uppercase tracking-wider text-neutral-400">
          Vedlegg {vedlegg.length > 0 && `· ${vedlegg.length}`}
        </span>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="ml-auto inline-flex items-center gap-1.5 text-[11.5px] font-medium text-neutral-500 border border-[#E7E4DB] rounded-lg px-2.5 py-1 hover:border-neutral-400 hover:text-neutral-800 transition-colors"
          data-testid="sak-vedlegg-velg"
        >
          <UploadCloud size={12} /> Last opp
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => { lastOpp(e.target.files); e.target.value = ""; }}
          data-testid="sak-vedlegg-input"
        />
      </div>

      {drar && (
        <div className="absolute inset-0 z-10 rounded-xl border-2 border-dashed border-emerald-500 bg-emerald-50/90 flex items-center justify-center pointer-events-none">
          <span className="text-[13px] font-medium text-emerald-700">Slipp filene her</span>
        </div>
      )}

      {vedlegg.length === 0 && opplastinger.length === 0 ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full border border-dashed border-[#E7E4DB] rounded-xl py-5 text-center hover:border-neutral-400 transition-colors group"
          data-testid="sak-vedlegg-tom"
        >
          <Paperclip size={15} className="mx-auto text-neutral-300 group-hover:text-neutral-500 transition-colors" />
          <p className="mt-1.5 text-[12px] text-neutral-400">Dra filer hit eller <span className="font-medium text-neutral-600 underline underline-offset-2">velg filer</span></p>
          <p className="mt-0.5 text-[10.5px] text-neutral-300">PDF, Word, bilder m.m. · maks 25 MB</p>
        </button>
      ) : (
        <div className="divide-y divide-neutral-50">
          {vedlegg.map((att, i) => {
            const kat = vedleggKategori(att);
            const { Ikon, farge, bg } = IKON[kat];
            return (
              <div key={att.id} className="group flex items-center gap-3 py-2" data-testid={`sak-vedlegg-rad-${att.id}`}>
                <button
                  type="button"
                  onClick={() => setViserIdx(i)}
                  className={`w-9 h-9 rounded-lg ${bg} ${farge} flex items-center justify-center shrink-0 hover:scale-105 transition-transform`}
                  title="Forhåndsvis"
                  data-testid={`sak-vedlegg-ikon-${att.id}`}
                >
                  <Ikon size={16} strokeWidth={1.8} />
                </button>
                <button type="button" onClick={() => setViserIdx(i)} className="flex-1 min-w-0 text-left" data-testid={`sak-vedlegg-navn-${att.id}`}>
                  <p className="text-[13px] font-medium text-neutral-900 truncate group-hover:underline underline-offset-2 decoration-neutral-300">{att.name}</p>
                  <p className="text-[10.5px] text-neutral-400">{fmtBytes(att.size)} · {att.uploadedBy} · {fmtTid(att.at)}</p>
                </button>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button type="button" onClick={() => setViserIdx(i)} className="p-1.5 text-neutral-400 hover:text-neutral-800 transition-colors" title="Forhåndsvis" data-testid={`sak-vedlegg-vis-${att.id}`}>
                    <Eye size={14} />
                  </button>
                  <a
                    href={`${API}/admin/tasks/${sak.id}/attachments/${att.id}/file?nedlast=1`}
                    className="p-1.5 text-neutral-400 hover:text-neutral-800 transition-colors"
                    title="Last ned"
                    data-testid={`sak-vedlegg-nedlast-${att.id}`}
                  >
                    <Download size={14} />
                  </a>
                  <button type="button" onClick={() => slett(att)} className="p-1.5 text-neutral-400 hover:text-red-500 transition-colors" title="Slett" data-testid={`sak-vedlegg-slett-${att.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
          {opplastinger.map((o) => (
            <div key={o.id} className="flex items-center gap-3 py-2" data-testid="sak-vedlegg-opplasting">
              <span className="w-9 h-9 rounded-lg bg-neutral-100 text-neutral-400 flex items-center justify-center shrink-0">
                <Loader2 size={15} className="animate-spin" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-neutral-700 truncate">{o.navn}</p>
                <div className="mt-1 h-1 rounded-full bg-neutral-100 overflow-hidden">
                  <div className="h-full rounded-full bg-neutral-900 transition-all duration-200" style={{ width: `${o.pct}%` }} />
                </div>
              </div>
              <span className="bud-mono text-[10.5px] tabular-nums text-neutral-400 shrink-0">{o.pct}%</span>
            </div>
          ))}
        </div>
      )}

      {viserIdx !== null && vedlegg[viserIdx] && (
        <VedleggViser sak={sak} vedlegg={vedlegg} startIndex={viserIdx} onClose={() => setViserIdx(null)} />
      )}
    </div>
  );
}
